// ============================================================
// Modelo COSHH Essentials (NTP 936, 2012) — evaluación cualitativa
// simplificada del riesgo por inhalación de agentes químicos.
// Fuente: Notas Técnicas de Prevención 936, INSHT.
// ============================================================

export type CoshhPeligrosidad = 'A' | 'B' | 'C' | 'D' | 'E'
export type CoshhVolatilidad = 'baja' | 'media' | 'alta'
export type CoshhCantidad = 'pequena' | 'mediana' | 'grande'
export type CoshhEstadoFisico = 'liquido' | 'solido'

export interface CoshhHPhrase {
  code: string
  text: string
  categoria: CoshhPeligrosidad
}

// Tabla 1B del NTP 936 — Clasificación de peligrosidad según frases H
export const COSHH_H_PHRASES: CoshhHPhrase[] = [
  { code: 'H303', text: 'Puede ser nocivo en caso de ingestión', categoria: 'A' },
  { code: 'H304', text: 'Puede ser mortal en caso de ingestión y penetración en las vías respiratorias', categoria: 'A' },
  { code: 'H305', text: 'Puede ser nocivo en caso de ingestión y penetración en las vías respiratorias', categoria: 'A' },
  { code: 'H313', text: 'Puede ser nocivo en contacto con la piel', categoria: 'A' },
  { code: 'H315', text: 'Provoca irritación cutánea', categoria: 'A' },
  { code: 'H316', text: 'Provoca una ligera irritación cutánea', categoria: 'A' },
  { code: 'H319', text: 'Provoca irritación ocular grave', categoria: 'A' },
  { code: 'H320', text: 'Provoca irritación ocular', categoria: 'A' },
  { code: 'H333', text: 'Puede ser nocivo si se inhala', categoria: 'A' },
  { code: 'H336', text: 'Puede provocar somnolencia o vértigo', categoria: 'A' },
  { code: 'H302', text: 'Nocivo en caso de ingestión', categoria: 'B' },
  { code: 'H312', text: 'Nocivo en contacto con la piel', categoria: 'B' },
  { code: 'H332', text: 'Nocivo en caso de inhalación', categoria: 'B' },
  { code: 'H371', text: 'Puede provocar daños en los órganos', categoria: 'B' },
  { code: 'H301', text: 'Tóxico en caso de ingestión', categoria: 'C' },
  { code: 'H311', text: 'Tóxico en contacto con la piel', categoria: 'C' },
  { code: 'H314', text: 'Provoca quemaduras graves en la piel y lesiones oculares graves', categoria: 'C' },
  { code: 'H317', text: 'Puede provocar una reacción alérgica en la piel', categoria: 'C' },
  { code: 'H318', text: 'Provoca lesiones oculares graves', categoria: 'C' },
  { code: 'H331', text: 'Tóxico en caso de inhalación', categoria: 'C' },
  { code: 'H335', text: 'Puede irritar las vías respiratorias', categoria: 'C' },
  { code: 'H370', text: 'Provoca daños en los órganos', categoria: 'C' },
  { code: 'H373', text: 'Puede provocar daños en los órganos tras exposiciones prolongadas o repetidas', categoria: 'C' },
  { code: 'H300', text: 'Mortal en caso de ingestión', categoria: 'D' },
  { code: 'H310', text: 'Mortal en contacto con la piel', categoria: 'D' },
  { code: 'H330', text: 'Mortal si se inhala', categoria: 'D' },
  { code: 'H351', text: 'Se sospecha que provoca cáncer', categoria: 'D' },
  { code: 'H360', text: 'Puede perjudicar la fertilidad o dañar al feto', categoria: 'D' },
  { code: 'H361', text: 'Se sospecha que perjudica la fertilidad o daña al feto', categoria: 'D' },
  { code: 'H362', text: 'Puede perjudicar a los niños alimentados con leche materna', categoria: 'D' },
  { code: 'H372', text: 'Provoca daños en los órganos tras exposiciones prolongadas o repetidas', categoria: 'D' },
  { code: 'H334', text: 'Puede provocar síntomas de alergia o asma o dificultades respiratorias en caso de inhalación', categoria: 'E' },
  { code: 'H340', text: 'Puede provocar defectos genéticos', categoria: 'E' },
  { code: 'H341', text: 'Se sospecha que provoca defectos genéticos', categoria: 'E' },
  { code: 'H350', text: 'Puede provocar cáncer', categoria: 'E' },
]

export function calcularPeligrosidad(hPhrases: string[]): CoshhPeligrosidad | null {
  if (hPhrases.length === 0) return null
  const orden: CoshhPeligrosidad[] = ['A', 'B', 'C', 'D', 'E']
  let maxIdx = -1
  for (const code of hPhrases) {
    const found = COSHH_H_PHRASES.find(p => p.code === code)
    if (found) {
      const idx = orden.indexOf(found.categoria)
      if (idx > maxIdx) maxIdx = idx
    }
  }
  return maxIdx >= 0 ? orden[maxIdx] : null
}

// Tabla 5 del NTP 936 — Determinación del nivel de riesgo potencial
// Clave: `${peligrosidad}-${volatilidad}-${cantidad}`
const TABLA_5: Record<string, number> = {
  'A-baja-pequena': 1,        'A-media_liquido-pequena': 1, 'A-media_solido-pequena': 1, 'A-alta-pequena': 1,
  'A-baja-mediana': 1,        'A-media_liquido-mediana': 1, 'A-media_solido-mediana': 1, 'A-alta-mediana': 2,
  'A-baja-grande': 1,         'A-media_liquido-grande': 1,  'A-media_solido-grande': 2,  'A-alta-grande': 2,

  'B-baja-pequena': 1,        'B-media_liquido-pequena': 1, 'B-media_solido-pequena': 1, 'B-alta-pequena': 1,
  'B-baja-mediana': 1,        'B-media_liquido-mediana': 2, 'B-media_solido-mediana': 2, 'B-alta-mediana': 2,
  'B-baja-grande': 1,         'B-media_liquido-grande': 2,  'B-media_solido-grande': 3,  'B-alta-grande': 3,

  'C-baja-pequena': 1,        'C-media_liquido-pequena': 2, 'C-media_solido-pequena': 1, 'C-alta-pequena': 2,
  'C-baja-mediana': 2,        'C-media_liquido-mediana': 3, 'C-media_solido-mediana': 3, 'C-alta-mediana': 3,
  'C-baja-grande': 2,         'C-media_liquido-grande': 4,  'C-media_solido-grande': 4,  'C-alta-grande': 4,

  'D-baja-pequena': 2,        'D-media_liquido-pequena': 3, 'D-media_solido-pequena': 2, 'D-alta-pequena': 3,
  'D-baja-mediana': 3,        'D-media_liquido-mediana': 4, 'D-media_solido-mediana': 4, 'D-alta-mediana': 4,
  'D-baja-grande': 3,         'D-media_liquido-grande': 4,  'D-media_solido-grande': 4,  'D-alta-grande': 4,
}

export interface CoshhResultado {
  nivel: 1 | 2 | 3 | 4
  accion: string
}

const ACCIONES: Record<1 | 2 | 3 | 4, string> = {
  1: 'Ventilación general. El control de la exposición podrá lograrse normalmente mediante el empleo de ventilación general.',
  2: 'Extracción localizada. Habrá que recurrir a medidas específicas de prevención (instalación de extracción localizada, diseño por proveedor especializado).',
  3: 'Confinamiento o sistemas cerrados. La sustancia no debe poder pasar a la atmósfera durante operaciones ordinarias; mantener el proceso a presión inferior a la atmosférica si es posible.',
  4: 'Asesoramiento de experto obligatorio. Requiere medidas específicamente diseñadas para el proceso y evaluación cuantitativa de la exposición.',
}

export function calcularNivelCoshh(
  peligrosidad: CoshhPeligrosidad,
  volatilidad: CoshhVolatilidad,
  cantidad: CoshhCantidad,
  estadoFisico: string | null
): CoshhResultado {
  if (peligrosidad === 'E') {
    return { nivel: 4, accion: ACCIONES[4] }
  }
  const volKey = volatilidad === 'media'
    ? (estadoFisico === 'solido' ? 'media_solido' : 'media_liquido')
    : volatilidad
  const key = `${peligrosidad}-${volKey}-${cantidad}`
  const nivel = (TABLA_5[key] ?? 4) as 1 | 2 | 3 | 4
  return { nivel, accion: ACCIONES[nivel] }
}

// Figura 2 del NTP 936 — Clasificación de volatilidad para líquidos,
// según punto de ebullición y temperatura de proceso
export function calcularVolatilidadLiquido(
  puntoEbullicionC: number,
  temperaturaProcesoC: number
): CoshhVolatilidad {
  const rectaAlta = 2 * puntoEbullicionC + 10
  const rectaMedia = 5 * puntoEbullicionC + 50
  if (temperaturaProcesoC >= rectaAlta) return 'alta'
  if (temperaturaProcesoC >= rectaMedia) return 'media'
  return 'baja'
}

export interface ChemicalProduct {
  id: string
  name: string
  coshh_h_phrases: string[]
  coshh_estado_fisico: string | null
  coshh_punto_ebullicion: number | null
}

export function calcularVolatilidadProducto(
  producto: ChemicalProduct,
  temperaturaProcesoC: number,
  pulverulenciaManual: CoshhVolatilidad | null
): CoshhVolatilidad | null {
  if (producto.coshh_estado_fisico === 'liquido' && producto.coshh_punto_ebullicion != null) {
    return calcularVolatilidadLiquido(producto.coshh_punto_ebullicion, temperaturaProcesoC)
  }
  if (producto.coshh_estado_fisico === 'solido') {
    return pulverulenciaManual
  }
  return null
}
